export const maxZ = 21
export const modalZ = maxZ + 2
export const coveredModalZ = 100

function interactableReducer(state = {}, action = {}) {
  let newWindows = {}
  let newState = {}
  let width
  let height
  let screen
  let newScreen
  let verticalOffset
  let horizontalOffset

  switch (action.type) {
    case 'UPDATE_INTERACTABLE_CONTAINER':
      width = parseInt(action.width.replace('px', ''))
      height = parseInt(action.height.replace('px', ''))

      verticalOffset = action.verticalOffset
      if (typeof verticalOffset === 'string' && verticalOffset !== 'loading') {
        verticalOffset = parseInt(verticalOffset.replace('px'))
      }
      horizontalOffset = action.horizontalOffset
      if (typeof horizontalOffset === 'string' && horizontalOffset !== 'loading') {
        horizontalOffset = parseInt(horizontalOffset.replace('px'))
      }
      // we recalculate the window sizes whenever this is reset to enable scaling of
      // windows as the window resizes
      Object.keys(state.windows).forEach(key => {
        const window = { ...state.windows[key] }
        newWindows[window.ref] = percentAdjust(window, state.container, {
          width,
          height,
          verticalOffset,
          horizontalOffset
          // only do scalingAndOffsets for Set, not update
        })
      })
      return {
        ...state,
        windows: newWindows,
        container: {
          width,
          height,
          verticalOffset,
          horizontalOffset
        }
      }
    case 'FORCE_UPDATE_WIDGET':
    case 'UPDATE_WIDGET':
      if (state.windows[action.window.ref] != null) {
        if (action.type === 'FORCE_UPDATE_WIDGET') {
          // Force update is only used by interactable because it should always
          // be able to update windows during drag and resize
          //
          // actually... now its used by the updateWindow() action so that we
          // can pass info via windowConfig
          newWindows = {
            ...state.windows,
            [action.window.ref]: {
              ...state.windows[action.window.ref],
              ...action.window
            }
          }
          newState = { ...state, windows: newWindows }
        } else {
          // If the window is already open, don't reopen it. This might be a case for
          // adding z-index tracking to redux though so we could pop the window to the
          // front in case the user lost it?
          newState = zIndexHandler(state, action.window.ref)
        }
      } else {
        const adjustedWindow = percentAdjust(
          action.window,
          // for next and previous container we will use the current container value but on the "current container" we will set offsets to zero
          // this way the offsets will be applied to the new view/screen since at this point, the containers is done calculating and all of the
          // offsets are relative to the previous position.
          { ...state.container, verticalOffset: 0, horizontalOffset: 0 },
          state.container
        )
        // search all open windows for any that have the same upper left corner as the one
        // that we are opening and if found, we will offset this window 10px down and over
        // to tile the windows.
        Object.keys(state.windows).forEach(windowRef => {
          const windowInstance = state.windows[windowRef]
          if (
            adjustedWindow.x === windowInstance.x &&
            adjustedWindow.y === windowInstance.y
          ) {
            // since this window has the same upper left as one thats already open
            // shift it down and to the right 10px to "tile" the windows for the
            // user
            adjustedWindow.x += 10
            adjustedWindow.y += 10
          }
        })
        newState = {
          ...state,
          windows: {
            ...state.windows,
            [action.window.ref]: adjustedWindow
          }
        }
      }
      return newState
    case 'SET_VIEW':
      screen = { ...action.screen }
      newScreen = {}
      if (Array.isArray(screen.windows)) {
        screen.windows.forEach((window) => {
          newScreen[window.ref] = {
            ...percentAdjust(
              window,
              // for next and previous container we will use the current container value but on the "current container" we will set offsets to zero
              // this way the offsets will be applied to the new view/screen since at this point, the containers is done calculating and all of the
              // offsets are relative to the previous position.
              { ...state.container, verticalOffset: 0, horizontalOffset: 0 },
              state.container
            ),
            zIndex: window.zIndex
          }
        })
      }
      return { ...state, windows: newScreen }
    case 'CLOSE_WIDGET':
      newState = zIndexDeleteHandler(state, action.ref)
      return newState
    case 'SET_WIDGET_ZINDEX':
      newState = {
        ...state,
        windows: {
          ...state.windows,
          [action.ref]: {
            ...state.windows[action.ref],
            zIndex: action.zIndex
          }
        }
      }

      return newState
    case 'UPDATE_DESIGN_MODE':
      newState = {
        ...state,
        uiDesignMode: action.designMode
      }

      return newState
    default:
      return state
  }
}

const zIndexHandler = (state, refName) => {
  const windows = { ...state.windows }

  const clickedWindow = windows[refName] || {}
  const clickedZ = clickedWindow.zIndex

  for (const windowName in windows) {
    if (windows[windowName]) {
      if (
        windows[windowName].modalCapable
      ) {
        // this window is being displayed as a modal if its the modal clicked on
        // it should be the highest modalZ but if its any other modal it should
        // just be that index -1 (the only other modal zIndex)
        if (
          windowName === refName &&
          windows[windowName].zIndex !== modalZ
        ) {
          windows[windowName].zIndex = modalZ
        } else if (
          windowName !== refName &&
          windows[windowName].zIndex !== modalZ - 1
        ) {
          windows[windowName].zIndex = modalZ - 1
        }
      } else if (
        windowName === refName &&
        windows[windowName].zIndex !== maxZ
      ) {
        // this was the window that was clicked and should be set to the maxZ
        windows[windowName].zIndex = maxZ
      } else if (windowName !== refName) {
        // this condition means we are looking one of the windows that is open but
        // wasn't the one that was clicked and that isn't a modal. So, if it was previously
        // a higher than the zIndex of the clicked window, we should reduce it by 1, otherwise
        // just leave it alone
        const oldZ = windows[windowName].zIndex || 0
        const newZ = Math.max(0, oldZ - 1) // no less than 0
        if (
          clickedZ === undefined ||
          (
            clickedZ <= oldZ &&
            windows[windowName].zIndex !== newZ
          )
        ) {
          windows[windowName].zIndex = newZ
        }
      }
    }
  }

  return { ...state, windows }
}

const zIndexDeleteHandler = (state, refName) => {
  const windows = { ...state.windows }

  const deletedWindow = windows[refName] || {}
  const deletedZ = deletedWindow.zIndex

  delete windows[refName]

  // window has been removed from list
  // shift all windows below it up one level
  for (const windowName in windows) {
    if (
      windows[windowName] &&
      !windows[windowName].modalCapable &&
      deletedZ > windows[windowName].zIndex
    ) {
      windows[windowName].zIndex = windows[windowName].zIndex + 1
    }
  }
  return { ...state, windows }
}

export default interactableReducer

const percentAdjust = (lastWindow, container, nextContainer = {}) => {
  const window = {
    ...lastWindow,
    width: lastWindow.width != null ? lastWindow.width : '50%',
    height: lastWindow.height != null ? lastWindow.height : '50%',
    x: lastWindow.x != null ? lastWindow.x : '5%',
    y: lastWindow.y != null ? lastWindow.y : '5%'
  }

  const config = { ...container, ...nextContainer }
  const loading =
    config.verticalOffset === 'loading' || config.horizontalOffset === 'loading'

  if (!loading) {
    return scaleOffsetCompensation(window, container, nextContainer)
  } else {
    return window
  }
}

//
// This is complicated because it lies at the intersection of multiple complicated things
//
// 1) windows can have their x/y/width/height specified as % (the way they are stored
//    in the database so they will load the same on different screen sizes) or in pixels
//    (when there is no %) because the window size/location has been modified since
//    the initial load.
// 2) The window container supports horizontal and vertical offsets (example of vertical offset
//    is a fixed offset applied to compensate for the patient header when on patient display,
//    vertical offset is future use). Additionally, these offsets may not be calculated yet
//    on the initial load (the values are 'loading' in this case) and may dynamically change
// 3) the browser window may be scaled and the individual windows within the screen should
//    dynamically scale in relation so they maintain the same proportion of the browser window
//    regardless of its size.
//
//    Useful test cases in case this ever has to be changed:
//    1) nurseDashboard vs patientDisplay (test vertical offset)
//    2) browser window resizing
//    3) Ensure that windows launched from other windows open correctly (example: skintest list
//       window on dashboard view button opens patient display skintest window in correct location)
//       particularly on patient display where offsets are involved
//    4) if you are not in a screen (check url) and open several window and then ctrl-r reload do
//       the window re-open in the correct locations after the reload (particularly on patient display
//       where offsets are involved)
//
export const scaleOffsetCompensation = (windowConfig, container, nextContainer) => {
    // calculate scales and offsets
    const xOffsetNew = isNaN(nextContainer?.horizontalOffset) ? 0 : nextContainer.horizontalOffset
    const yOffsetNew = isNaN(nextContainer?.verticalOffset) ? 0 : nextContainer.verticalOffset
    const xOffsetOld = isNaN(container?.horizontalOffset) ? 0 : container.horizontalOffset
    const yOffsetOld = isNaN(container?.verticalOffset) ? 0 : container.verticalOffset

    const unsafeHorizontalScaleFactor = (nextContainer.width - xOffsetNew) / (container.width - xOffsetOld)
    const unsafeVerticalScaleFactor = (nextContainer.height - yOffsetNew) / (container.height - yOffsetOld)
    
    const horizontalScaleFactor = isNaN(unsafeHorizontalScaleFactor) ? 1 : unsafeHorizontalScaleFactor
    const verticalScaleFactor = isNaN(unsafeVerticalScaleFactor) ? 1 : unsafeVerticalScaleFactor

    const width = nextContainer.width - nextContainer.horizontalOffset
    const height = nextContainer.height - nextContainer.verticalOffset

    // now modify the window as necessary to apply new scales and offsets
    const newWindowConfig = {...windowConfig}

    newWindowConfig.x = scaleWindowProperty(windowConfig.x, width, xOffsetNew, xOffsetOld, horizontalScaleFactor)
    newWindowConfig.width = scaleWindowProperty(windowConfig.width, width, 0, 0, horizontalScaleFactor)
    newWindowConfig.y = scaleWindowProperty(windowConfig.y, height, yOffsetNew, yOffsetOld, verticalScaleFactor)
    newWindowConfig.height = scaleWindowProperty(windowConfig.height, height, 0, 0, verticalScaleFactor)
  
    return newWindowConfig
}

const scaleWindowProperty = (value, availalbeSpace, offsetNew, offsetOld, scaleFactor) => {
  if(typeof value === 'string' && value.includes('%')) {
    // do percent based scaling on new container
    return offsetNew + (availalbeSpace * (parseInt(value.replace('%',''))/100))
  } else {
    // do pixel based relative scaling
    return offsetNew + ((value - offsetOld) * scaleFactor)
  }
}